import React, { useState, useEffect } from 'react';
import { Palette, Check, Loader2, AlertCircle, Type, MessageSquare, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { withBackend } from '../utils/api';

export default function SharedBrandView({ userId }) {
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [claimedName, setClaimedName] = useState('');
  const [customName, setCustomName] = useState('');
  const [showNameInput, setShowNameInput] = useState(false);

  const token = window.location.pathname.split('/brand/share/')[1];

  useEffect(() => {
    if (!token) { setError('Invalid share link'); setLoading(false); return; }
    fetch(withBackend(`/api/brand-identities/shared/${token}`))
      .then(r => {
        if (!r.ok) throw new Error(r.status === 410 ? 'This share link has expired' : 'Share link not found');
        return r.json();
      })
      .then(data => { setBrand(data.brand); setCustomName(data.brand.name); })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  const handleClaim = async () => {
    if (!userId) {
      toast.error('Please sign in to add this brand identity');
      return;
    }
    setClaiming(true);
    try {
      const resp = await fetch(withBackend(`/api/brand-identities/shared/${token}/claim`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, name: customName || undefined }),
      });
      if (!resp.ok) {
        const err = await resp.json();
        throw new Error(err.error || 'Failed to add brand identity');
      }
      const result = await resp.json();
      setClaimed(true);
      setClaimedName(result.name);
      toast.success(`"${result.name}" added to your brand identities!`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setClaiming(false);
    }
  };

  const getColors = (bi) => {
    const semantic = [bi.color_background, bi.color_surface, bi.color_text_primary, bi.color_text_body, bi.color_accent, bi.color_border].filter(Boolean);
    if (semantic.length >= 2) return semantic;
    return [bi.primary_color, bi.secondary_color, bi.accent_color].filter(Boolean);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md px-6">
          <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <h1 className="text-xl font-semibold mb-2">Unable to load brand</h1>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (!brand) return null;

  const colors = getColors(brand);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-lg">
        {/* Brand preview card */}
        <div className="bg-card border border-border rounded-2xl shadow-xl overflow-hidden">
          {/* Color strip */}
          {colors.length > 0 && (
            <div className="flex h-3">
              {colors.map((c, i) => (
                <div key={i} className="flex-1" style={{ backgroundColor: c }} />
              ))}
            </div>
          )}

          <div className="p-8">
            {/* Header */}
            <div className="flex items-center gap-4 mb-6">
              {brand.logo_url ? (
                <img src={brand.logo_url} alt={brand.name} className="w-14 h-14 object-contain rounded-lg border border-border bg-white p-1" />
              ) : (
                <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Palette className="w-7 h-7 text-primary" />
                </div>
              )}
              <div>
                <h1 className="text-xl font-bold text-foreground">{brand.name}</h1>
                <p className="text-sm text-muted-foreground">Shared brand identity</p>
              </div>
            </div>

            {/* Brand details */}
            <div className="space-y-4 mb-8">
              {/* Colors */}
              {colors.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2">Colors</p>
                  <div className="flex gap-2">
                    {colors.map((c, i) => (
                      <div key={i} className="flex items-center gap-1.5">
                        <div className="w-6 h-6 rounded-full border border-border shadow-sm" style={{ backgroundColor: c }} />
                        <span className="text-xs text-muted-foreground">{c}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Fonts */}
              {(brand.font_heading || brand.font_body) && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
                    <Type className="w-3.5 h-3.5" /> Fonts
                  </p>
                  <div className="flex gap-4 text-sm">
                    {brand.font_heading && <span>Heading: <strong>{brand.font_heading}</strong></span>}
                    {brand.font_body && <span>Body: <strong>{brand.font_body}</strong></span>}
                  </div>
                </div>
              )}

              {/* Tone */}
              {brand.tone_of_voice && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
                    <MessageSquare className="w-3.5 h-3.5" /> Tone of Voice
                  </p>
                  <p className="text-sm text-foreground/80 line-clamp-3">{brand.tone_of_voice}</p>
                </div>
              )}

              {/* Tagline */}
              {brand.tagline && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Tagline</p>
                  <p className="text-sm italic text-foreground/80">"{brand.tagline}"</p>
                </div>
              )}
            </div>

            {/* Claim section */}
            {claimed ? (
              <div className="flex items-center gap-3 p-4 rounded-xl bg-green-500/10 border border-green-500/20">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-green-700">Added to your brands!</p>
                  <p className="text-xs text-green-600">Saved as "{claimedName}"</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="ml-auto"
                  onClick={() => { window.location.href = '/brand-identities'; }}
                >
                  View brands
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {showNameInput && (
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Save as (rename if you want):</label>
                    <Input
                      value={customName}
                      onChange={e => setCustomName(e.target.value)}
                      placeholder={brand.name}
                      className="text-sm"
                    />
                  </div>
                )}
                <div className="flex gap-2">
                  <Button
                    onClick={handleClaim}
                    disabled={claiming || !userId}
                    className="flex-1 bg-primary hover:bg-primary/90"
                  >
                    {claiming ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Adding...</>
                    ) : (
                      <><Palette className="w-4 h-4 mr-2" /> Add to my brands</>
                    )}
                  </Button>
                  {!showNameInput && (
                    <Button variant="outline" size="sm" onClick={() => setShowNameInput(true)} title="Rename before adding">
                      Rename
                    </Button>
                  )}
                </div>
                {!userId && (
                  <p className="text-xs text-muted-foreground text-center">
                    Sign in to add this brand identity to your account
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground mt-6">
          Shared via <a href="/" className="text-primary hover:underline">wizardly.ai</a>
        </p>
      </div>
    </div>
  );
}
